import { diffArrays } from 'diff';
import {
  ECodeBlockType,
  EDiffStatus,
  type ICodeBlock,
  type IBlockDiff,
  type IDiffChangeSummary,
  type IRowDiff,
  type ISection,
} from './diff-types';

// =============================================================================
// Constants
// =============================================================================

/** Regex patterns for parsing markdown */
const HEADER_PATTERN = /^(#{1,6})\s+([^\n]+)$/;
// Capture leading indent (group 1), marker (group 2), and content (group 3)
// separately so the item's nesting depth can be derived from its indentation.
const LIST_PATTERN = /^(\s*)([-*+]|\d+\.)\s+([^\n]+)$/;
const ORDERED_LIST_MARKER_PATTERN = /^\d+\.$/;
/** A GFM table separator cell, e.g. `---`, `:---`, `---:`, `:---:`. */
const TABLE_SEPARATOR_CELL_PATTERN = /^:?-+:?$/;

/** Regex to match HTML comments (e.g., <!-- @vars: [...] -->) */
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;

// =============================================================================
// Table Parsing Helpers
// =============================================================================

/** Splits a `| a | b |` row into trimmed cell strings, dropping the outer pipes. */
function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map(cell => cell.trim());
}

/** A candidate table row: non-empty and pipe-delimited on both ends. */
function isTableRowLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 1;
}

/** The `| --- | --- |`-style row that marks the second line of a GFM table. */
function isTableSeparatorLine(line: string): boolean {
  if (!isTableRowLine(line)) return false;
  const cells = splitTableRow(line);
  return (
    cells.length > 0 &&
    cells.every(cell => TABLE_SEPARATOR_CELL_PATTERN.test(cell))
  );
}

// =============================================================================
// Block Diff Helpers
// =============================================================================

/**
 * Creates a block diff object with common properties.
 *
 * This is a factory function that constructs IBlockDiff objects with the
 * appropriate content key (oldContent or newContent) based on whether
 * the block is being added or removed.
 *
 * @param status - The diff status (ADDED, REMOVED, MODIFIED, UNCHANGED)
 * @param block - The source code block containing type, level, and content
 * @param contentKey - Which content property to populate ('oldContent' for removals, 'newContent' for additions)
 * @returns A block diff object ready for rendering
 */
function createBlockDiff(
  status: EDiffStatus,
  block: ICodeBlock,
  contentKey: 'oldContent' | 'newContent',
): IBlockDiff {
  return {
    status,
    blockType: block.type,
    level: block.level,
    depth: block.depth,
    [contentKey]: block.content,
  };
}

/**
 * Builds all-same-status row diffs for a wholly added/removed table. Without
 * this a pure add/remove table diff carries only raw markdown (no rowDiffs),
 * and the renderer (createTableDiffNode) has nothing to render — the table
 * would silently vanish from the diff.
 */
function wholeTableRowDiffs(
  block: ICodeBlock,
  status: EDiffStatus.ADDED | EDiffStatus.REMOVED,
): IRowDiff[] | undefined {
  if (block.type !== ECodeBlockType.TABLE || !block.rows) return undefined;
  const key = status === EDiffStatus.ADDED ? 'newCells' : 'oldCells';
  return block.rows.map((cells, rowIndex) => ({
    status,
    isHeader: rowIndex === 0,
    [key]: cells,
  }));
}

/**
 * Creates a block diff representing a removed block.
 *
 * Used when a block exists in the old document but not in the new one.
 * The block's content is stored in oldContent for rendering with deletion marks.
 *
 * @param block - The block that was removed from the document
 * @returns A block diff with REMOVED status and oldContent populated
 */
function createRemovedDiff(block: ICodeBlock): IBlockDiff {
  const diff = createBlockDiff(EDiffStatus.REMOVED, block, 'oldContent');
  const rowDiffs = wholeTableRowDiffs(block, EDiffStatus.REMOVED);
  if (rowDiffs) diff.rowDiffs = rowDiffs;
  return diff;
}

/**
 * Creates a block diff representing an added block.
 *
 * Used when a block exists in the new document but not in the old one.
 * The block's content is stored in newContent for rendering with addition marks.
 *
 * @param block - The block that was added to the document
 * @returns A block diff with ADDED status and newContent populated
 */
function createAddedDiff(block: ICodeBlock): IBlockDiff {
  const diff = createBlockDiff(EDiffStatus.ADDED, block, 'newContent');
  const rowDiffs = wholeTableRowDiffs(block, EDiffStatus.ADDED);
  if (rowDiffs) diff.rowDiffs = rowDiffs;
  return diff;
}

// =============================================================================
// Parsing Functions
// =============================================================================

/**
 * Parses AOP markdown code into typed blocks.
 *
 * @param markdown - The markdown string to parse
 * @returns Array of parsed code blocks
 *
 * @example
 * ```
 * # Title
 * ## Required Information
 * - Item 1
 * - Item 2
 * ```
 * @returns Array of parsed code blocks [
 *   { type: 'heading', level: 1, content: 'Title' },
 *   { type: 'heading', level: 2, content: 'Required Information' },
 *   { type: 'orderedList', content: 'Item 1\nItem 2' },
 * ]
 */
export function parseAopCodeToBlocks(markdown: string): ICodeBlock[] {
  // Strip HTML comments (e.g., <!-- @vars: [...] -->) before parsing, and clean up any trialing whitespace
  // Replace comments with space (becomes empty after trimEnd, preserving line structure)
  const cleanedMarkdown = markdown
    .replace(HTML_COMMENT_PATTERN, ' ')
    .split('\n')
    .map(line => line.trimEnd())
    .join('\n');
  const lines = cleanedMarkdown.split('\n');
  const blocks: ICodeBlock[] = [];
  let paragraphLines: string[] = [];
  // Ascending stack of the leading-indent widths of the currently-open list's
  // ancestors. A list item's depth is `stack.length - 1` after slotting its
  // indent in. Reset whenever a non-list block (paragraph/heading/table) breaks
  // the list, so a later list restarts at depth 0.
  let listIndentStack: number[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;

    const content = paragraphLines.join('\n').trim();
    if (content) {
      blocks.push({ type: ECodeBlockType.PARAGRAPH, content });
      listIndentStack = [];
    }
    paragraphLines = [];
  };

  // Maps a list item's leading-indent width to a 0-based nesting depth using
  // the running indent stack (handles arbitrary/variable indent widths).
  const listDepthForIndent = (indent: number): number => {
    while (
      listIndentStack.length > 0 &&
      listIndentStack[listIndentStack.length - 1] > indent
    ) {
      listIndentStack.pop();
    }
    if (
      listIndentStack.length === 0 ||
      listIndentStack[listIndentStack.length - 1] < indent
    ) {
      listIndentStack.push(indent);
    }
    return listIndentStack.length - 1;
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Check for a GFM table: a `| ... |` row immediately followed by a
    // `| --- | --- |` separator row. Consume every subsequent `| ... |` row
    // as part of the same table block so it can be diffed and rendered as an
    // actual table instead of being swallowed into one plain-text paragraph.
    if (
      isTableRowLine(line) &&
      i + 1 < lines.length &&
      isTableSeparatorLine(lines[i + 1])
    ) {
      flushParagraph();
      const rawLines = [line, lines[i + 1]];
      const rows: string[][] = [splitTableRow(line)];
      i += 2;
      while (i < lines.length && isTableRowLine(lines[i])) {
        rows.push(splitTableRow(lines[i]));
        rawLines.push(lines[i]);
        i += 1;
      }
      listIndentStack = [];
      blocks.push({
        type: ECodeBlockType.TABLE,
        content: rawLines.join('\n'),
        rows,
      });
      continue;
    }

    // Check for header
    const headerMatch = line.match(HEADER_PATTERN);
    if (headerMatch) {
      flushParagraph();
      listIndentStack = [];
      blocks.push({
        type: ECodeBlockType.HEADING,
        level: headerMatch[1].length,
        content: headerMatch[2],
      });
      i += 1;
      continue;
    }

    // Check for list item (ordered or unordered)
    const listMatch = line.match(LIST_PATTERN);
    if (listMatch) {
      flushParagraph();
      const indent = listMatch[1].length;
      const marker = listMatch[2];
      const isOrdered = ORDERED_LIST_MARKER_PATTERN.test(marker);
      // Always advance the indent stack (so sibling depths stay correct), but
      // only tag the block when it's actually nested — top-level items omit
      // `depth` so the common case matches the pre-nesting block shape.
      const depth = listDepthForIndent(indent);
      blocks.push({
        type: isOrdered
          ? ECodeBlockType.ORDERED_LIST
          : ECodeBlockType.UNORDERED_LIST,
        content: listMatch[3],
        ...(depth > 0 ? { depth } : {}),
      });
      i += 1;
      continue;
    }

    // Empty line flushes paragraph
    if (line.trim() === '') {
      flushParagraph();
      i += 1;
      continue;
    }

    // Accumulate paragraph lines
    paragraphLines.push(line);
    i += 1;
  }

  flushParagraph();
  return blocks;
}

/**
 * Groups blocks into sections based on headers.
 *
 * @param blocks - Array of code blocks to group
 * @returns Array of sections
 *
 * @example
 * ```
 * # Title
 * ## Required Information
 * - Item 1
 * - Item 2
 * ```
 * @returns Array of sections [
 *   { header: { type: 'heading', level: 1, content: 'Title' }, content: [
 *     { type: 'heading', level: 2, content: 'Required Information' },
 *     { type: 'orderedList', content: 'Item 1\nItem 2' },
 *   ] },
 * ]
 */
// Only top-level headings (# / ##) start a new section. Deeper headings
// (### and below — e.g. `### 14. Step title`) stay as CONTENT inside their
// section, so the block-level LCS diff (which is ordinal-aware) handles steps
// being merged/removed/renumbered without the positional section pairing
// cascading a "changed" mark through every later step.
const SECTION_HEADING_MAX_LEVEL = 2;

function groupBlocksIntoSections(blocks: ICodeBlock[]): ISection[] {
  const sections: ISection[] = [];
  let current: ISection = { header: null, content: [] };

  for (const block of blocks) {
    if (
      block.type === ECodeBlockType.HEADING &&
      (block.level ?? 1) <= SECTION_HEADING_MAX_LEVEL
    ) {
      if (current.header || current.content.length > 0) {
        sections.push(current);
      }
      current = { header: block, content: [] };
    } else {
      current.content.push(block);
    }
  }

  if (current.header || current.content.length > 0) {
    sections.push(current);
  }

  return sections;
}

// =============================================================================
// Section Diff Functions
// =============================================================================

/** Table rows are compared cell-by-cell for equality (used by the LCS row diff below). */
function rowsEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((cell, i) => cell === b[i]);
}

/**
 * Diffs a table block's rows with the same LCS approach as `diffSectionContent`
 * (row insertion/removal doesn't cascade into every subsequent row), then
 * pairs up mismatched rows positionally within their own replacement window
 * to mark them MODIFIED (cell-level rendering happens downstream).
 */
function diffTableRows(oldRows: string[][], newRows: string[][]): IRowDiff[] {
  const result: IRowDiff[] = [];
  const changes = diffArrays(oldRows, newRows, { comparator: rowsEqual });

  const pushRow = (
    status: EDiffStatus,
    oldCells: string[] | undefined,
    newCells: string[] | undefined,
    isHeader: boolean,
  ) => {
    result.push({ status, isHeader, oldCells, newCells });
  };

  let rowIndex = 0;
  let i = 0;
  while (i < changes.length) {
    const change = changes[i];

    if (!change.added && !change.removed) {
      for (const row of change.value) {
        pushRow(EDiffStatus.UNCHANGED, row, row, rowIndex === 0);
        rowIndex += 1;
      }
      i += 1;
      continue;
    }

    if (change.removed) {
      const removedRun = change.value;
      const nextChange = changes[i + 1];
      const addedRun = nextChange?.added ? nextChange.value : [];
      const pairLen = Math.max(removedRun.length, addedRun.length);

      for (let j = 0; j < pairLen; j++) {
        const oldRow = removedRun[j];
        const newRow = addedRun[j];
        const isHeader = rowIndex === 0;
        if (oldRow && newRow) {
          pushRow(
            rowsEqual(oldRow, newRow)
              ? EDiffStatus.UNCHANGED
              : EDiffStatus.MODIFIED,
            oldRow,
            newRow,
            isHeader,
          );
        } else if (oldRow) {
          pushRow(EDiffStatus.REMOVED, oldRow, undefined, isHeader);
        } else if (newRow) {
          pushRow(EDiffStatus.ADDED, undefined, newRow, isHeader);
        }
        rowIndex += 1;
      }

      i += addedRun.length > 0 ? 2 : 1;
      continue;
    }

    // Standalone addition (no preceding removal paired with it).
    for (const row of change.value) {
      pushRow(EDiffStatus.ADDED, undefined, row, rowIndex === 0);
      rowIndex += 1;
    }
    i += 1;
  }

  return result;
}

/** Diffs one matched (same-type) block pair — tables get row-level diffing, everything else content equality. */
function diffBlockPair(oldBlock: ICodeBlock, newBlock: ICodeBlock): IBlockDiff {
  if (oldBlock.type === ECodeBlockType.TABLE) {
    const rowDiffs = diffTableRows(oldBlock.rows ?? [], newBlock.rows ?? []);
    const isUnchanged = rowDiffs.every(r => r.status === EDiffStatus.UNCHANGED);
    return {
      status: isUnchanged ? EDiffStatus.UNCHANGED : EDiffStatus.MODIFIED,
      blockType: ECodeBlockType.TABLE,
      oldContent: oldBlock.content,
      newContent: newBlock.content,
      rowDiffs,
    };
  }

  return {
    status:
      oldBlock.content === newBlock.content
        ? EDiffStatus.UNCHANGED
        : EDiffStatus.MODIFIED,
    blockType: oldBlock.type,
    level: oldBlock.level,
    depth: oldBlock.depth,
    oldContent: oldBlock.content,
    newContent: newBlock.content,
  };
}

/** Exact block equality — the anchor for LCS alignment (see `diffSectionContent`). */
/** Leading ordinal like "14. " or "3) " — the visible step number. */
const LEADING_ORDINAL = /^\s*\d+[.)]\s+/;

/**
 * Block content with any leading ordinal stripped. Steps are often written as
 * `### 14. Title` headings, so merging/removing a step renumbers every later
 * heading. Ignoring the ordinal here keeps those renumbered-but-otherwise-
 * identical blocks aligned (unchanged) instead of cascading a "changed" mark
 * through the whole rest of the document.
 */
function contentSansOrdinal(block: ICodeBlock): string {
  return block.content.replace(LEADING_ORDINAL, '');
}

function blocksEqual(a: ICodeBlock, b: ICodeBlock): boolean {
  return (
    a.type === b.type &&
    a.level === b.level &&
    contentSansOrdinal(a) === contentSansOrdinal(b)
  );
}

/**
 * Diffs content blocks within a section using LCS alignment (via `diffArrays`)
 * instead of positional (index-by-index) comparison.
 *
 * Unchanged blocks anywhere in the sequence are recognized as unchanged even
 * when content was inserted/removed/reordered elsewhere — a change stays
 * localized to the region that actually changed instead of cascading into
 * every subsequent block, which is what a positional diff does.
 *
 * @param oldContent - Content blocks from old section (non-header blocks)
 * @param newContent - Content blocks from new section (non-header blocks)
 * @returns Array of block diffs representing changes between the two content arrays
 *
 * @example
 * // Same content unchanged
 * diffSectionContent(
 *   [{ type: 'paragraph', content: 'Hello' }],
 *   [{ type: 'paragraph', content: 'Hello' }]
 * )
 * // Returns: [{ status: 'unchanged', blockType: 'paragraph', ... }]
 *
 * @example
 * // Content modified
 * diffSectionContent(
 *   [{ type: 'paragraph', content: 'Hello' }],
 *   [{ type: 'paragraph', content: 'Hello World' }]
 * )
 * // Returns: [{ status: 'modified', oldContent: 'Hello', newContent: 'Hello World', ... }]
 */
function diffSectionContent(
  oldContent: ICodeBlock[],
  newContent: ICodeBlock[],
): IBlockDiff[] {
  const result: IBlockDiff[] = [];
  const changes = diffArrays(oldContent, newContent, {
    comparator: blocksEqual,
  });

  // Track positions in both arrays so a common run can pair the OLD block with
  // its NEW counterpart — the two can differ by a leading ordinal (a renumbered
  // step), and we want to render the NEW number without flagging a change.
  let oldIdx = 0;
  let newIdx = 0;
  let i = 0;
  while (i < changes.length) {
    const change = changes[i];

    if (!change.added && !change.removed) {
      // Comparator-equal run: identical, or differing only by a leading
      // ordinal. Emit as UNCHANGED but carry the NEW content so a renumbered
      // heading shows its new number with no diff mark.
      const len = change.value.length;
      for (let j = 0; j < len; j++) {
        const oldBlock = oldContent[oldIdx + j];
        const newBlock = newContent[newIdx + j];
        result.push(
          newBlock.type === ECodeBlockType.TABLE
            ? diffBlockPair(oldBlock, newBlock)
            : {
                status: EDiffStatus.UNCHANGED,
                blockType: newBlock.type,
                level: newBlock.level,
                depth: newBlock.depth,
                oldContent: oldBlock.content,
                newContent: newBlock.content,
              },
        );
      }
      oldIdx += len;
      newIdx += len;
      i += 1;
      continue;
    }

    if (change.removed) {
      // Pair this removed run against an immediately-following added run
      // (a "replacement" window) positionally, but *only within this window*
      // — so the cascade a positional diff would cause stays contained to
      // the blocks that actually changed.
      const removedRun = change.value;
      const nextChange = changes[i + 1];
      const addedRun = nextChange?.added ? nextChange.value : [];
      const pairLen = Math.max(removedRun.length, addedRun.length);

      for (let j = 0; j < pairLen; j++) {
        const oldBlock = removedRun[j];
        const newBlock = addedRun[j];

        if (oldBlock && newBlock) {
          const sameType =
            oldBlock.type === newBlock.type &&
            oldBlock.level === newBlock.level;
          result.push(
            sameType
              ? diffBlockPair(oldBlock, newBlock)
              : createRemovedDiff(oldBlock),
          );
          if (!sameType) result.push(createAddedDiff(newBlock));
        } else if (oldBlock) {
          result.push(createRemovedDiff(oldBlock));
        } else if (newBlock) {
          result.push(createAddedDiff(newBlock));
        }
      }

      oldIdx += removedRun.length;
      newIdx += addedRun.length;
      i += addedRun.length > 0 ? 2 : 1;
      continue;
    }

    // Standalone addition (no preceding removal consumed it).
    for (const block of change.value) {
      result.push(createAddedDiff(block));
    }
    newIdx += change.value.length;
    i += 1;
  }

  return result;
}

/**
 * Diffs a header block between two sections with special handling for structural headers.
 *
 * AOP documents have a consistent structure where level 2+ headers (## Required Information,
 * ## Process, etc.) should always appear unchanged. Only level 1 headers (# Title) show actual diff highlighting.
 *
 * @param oldHeader - The header block from the old section (null if section had no header)
 * @param newHeader - The header block from the new section (null if section had no header)
 * @returns A block diff for the header, or null if both headers are null
 *
 * @example
 * // Level 1 header modified - shows diff
 * diffHeader(
 *   { type: 'heading', level: 1, content: 'Old Title' },
 *   { type: 'heading', level: 1, content: 'New Title' }
 * )
 * // Returns: { status: 'modified', oldContent: 'Old Title', newContent: 'New Title', ... }
 *
 */
function diffHeader(
  oldHeader: ICodeBlock | null,
  newHeader: ICodeBlock | null,
): IBlockDiff | null {
  if (!oldHeader && !newHeader) return null;

  if (!oldHeader) {
    return createAddedDiff(newHeader!);
  }

  if (!newHeader) {
    return createRemovedDiff(oldHeader);
  }

  // Level 2+ headers are always unchanged (consistent structure)
  const STRUCTURAL_HEADING_LEVEL = 2;
  const isStructuralHeading = (level?: number) =>
    (level ?? 1) >= STRUCTURAL_HEADING_LEVEL;
  const isUnchanged =
    isStructuralHeading(oldHeader.level) ||
    oldHeader.content === newHeader.content;

  return {
    status: isUnchanged ? EDiffStatus.UNCHANGED : EDiffStatus.MODIFIED,
    blockType: ECodeBlockType.HEADING,
    level: oldHeader.level,
    oldContent: isStructuralHeading(oldHeader.level)
      ? newHeader.content
      : oldHeader.content,
    newContent: newHeader.content,
  };
}

/**
 * Computes diff between two AOP markdown documents.
 *
 * Since AOP documents have consistent structure:
 * - Level 2+ headers (## Required Information, etc.) are always unchanged
 * - Level 1 header (# Title) shows character-level diff
 * - Content within sections is compared with LCS-based block alignment (see
 *   diffSectionContent) -- that is where real restructuring happens (steps
 *   reordered, table rows inserted). Sections themselves stay positionally
 *   paired: AOP documents have a fixed, small set of top-level sections that
 *   are essentially never reordered, and diffHeader depends on that pairing
 *   to tell "this section was reworded" from "this section was replaced by
 *   an unrelated one".
 *
 * @param oldCode - The old version of the markdown code
 * @param newCode - The new version of the markdown code
 * @returns Array of block diffs
 */
export function computeStructuredDiff(
  oldCode: string,
  newCode: string,
): IBlockDiff[] {
  const oldSections = groupBlocksIntoSections(parseAopCodeToBlocks(oldCode));
  const newSections = groupBlocksIntoSections(parseAopCodeToBlocks(newCode));
  const result: IBlockDiff[] = [];
  const maxSections = Math.max(oldSections.length, newSections.length);

  for (let i = 0; i < maxSections; i++) {
    const oldSection = oldSections[i];
    const newSection = newSections[i];

    if (oldSection && newSection) {
      // Both sections exist - diff header and content
      const headerDiff = diffHeader(oldSection.header, newSection.header);
      if (headerDiff) result.push(headerDiff);
      result.push(
        ...diffSectionContent(oldSection.content, newSection.content),
      );
    } else if (oldSection) {
      // Section removed
      if (oldSection.header) {
        result.push(createRemovedDiff(oldSection.header));
      }
      result.push(...oldSection.content.map(createRemovedDiff));
    } else if (newSection) {
      // Section added
      if (newSection.header) {
        result.push(createAddedDiff(newSection.header));
      }
      result.push(...newSection.content.map(createAddedDiff));
    }
  }

  return result;
}

// =============================================================================
// Change Navigation
// =============================================================================

/** True for blocks that represent an actual change (not context). The single
 *  predicate both the sidebar summary and the anchor-stamping share so their
 *  sequential indices always line up. */
export function isChangedBlock(diff: IBlockDiff): boolean {
  return diff.status !== EDiffStatus.UNCHANGED;
}

const MARKDOWN_NOISE = /(\*\*|__|`|^#{1,6}\s+|^\s*[-*+]\s+|^\s*\d+\.\s+|\|)/g;
const CHANGE_LABEL_MAX = 60;

/** Turns a block's raw markdown into a compact, single-line label. */
function toChangeLabel(diff: IBlockDiff): string {
  const source =
    diff.status === EDiffStatus.REMOVED
      ? (diff.oldContent ?? '')
      : (diff.newContent ?? diff.oldContent ?? '');
  const flattened = source
    .replace(/\n+/g, ' ')
    .replace(MARKDOWN_NOISE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!flattened) {
    return diff.blockType === ECodeBlockType.TABLE ? 'Table' : 'Section';
  }
  return flattened.length > CHANGE_LABEL_MAX
    ? `${flattened.slice(0, CHANGE_LABEL_MAX).trimEnd()}…`
    : flattened;
}

/**
 * Extracts the navigable changes from a structured diff, in document order.
 * The Nth entry's `index` matches the `data-diff-anchor="N"` stamped on the
 * Nth changed block in the rendered TipTap diff (see diffToTipTapContent), so
 * a sidebar row can scroll the document to its block.
 */
export function getDiffChangeSummaries(
  diffs: IBlockDiff[],
): IDiffChangeSummary[] {
  const summaries: IDiffChangeSummary[] = [];
  // Increments for EVERY changed block, in lockstep with the renderer's anchor
  // stamping (diffToTipTapContent), so a summary's `index` always maps to the
  // right `data-diff-anchor` — even for blocks that are absorbed into a section
  // group and don't get their own summary.
  let changeIndex = 0;
  let i = 0;

  while (i < diffs.length) {
    const diff = diffs[i];
    if (!isChangedBlock(diff)) {
      i += 1;
      continue;
    }

    const anchor = changeIndex;
    changeIndex += 1;
    i += 1;

    // A whole section added or removed is one logical change: don't also list
    // the blocks inside it. When the leader is an added/removed heading, absorb
    // the blocks that make up its section — same status, and either non-headings
    // or DEEPER subheadings — into this one summary. Stop at a same-or-shallower
    // heading (the next sibling section), an unchanged block, or a different
    // kind of change. Absorbed blocks still advance changeIndex (the renderer
    // stamps them) but produce no separate row; the entry covers indices
    // anchor..endIndex so the sidebar can highlight/scroll the whole section.
    const isSectionLeader =
      diff.blockType === ECodeBlockType.HEADING &&
      (diff.status === EDiffStatus.ADDED ||
        diff.status === EDiffStatus.REMOVED);

    if (isSectionLeader) {
      const leaderLevel = diff.level ?? 1;
      while (i < diffs.length) {
        const next = diffs[i];
        if (!isChangedBlock(next)) break;
        if (next.status !== diff.status) break;
        if (
          next.blockType === ECodeBlockType.HEADING &&
          (next.level ?? 1) <= leaderLevel
        ) {
          break;
        }
        changeIndex += 1;
        i += 1;
      }
    }

    summaries.push({
      index: anchor,
      endIndex: changeIndex - 1,
      status: diff.status as IDiffChangeSummary['status'],
      label: toChangeLabel(diff),
    });
  }

  return summaries;
}

/**
 * Convenience wrapper: compute the change summaries directly from two markdown
 * documents (same inputs the diff editor renders from).
 */
export function computeDiffChangeSummaries(
  oldCode: string,
  newCode: string,
): IDiffChangeSummary[] {
  return getDiffChangeSummaries(computeStructuredDiff(oldCode, newCode));
}
